import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

export class ImageUploadService {
  /**
   * Upload a logo to Firebase Storage and return the download URL
   */
  static async uploadLogo(file: File): Promise<string> {
    try {
      console.log('Starting image upload for:', file.name);

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB');
      }

      // Create a unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const filename = `logo_${timestamp}.${fileExtension}`;
      const storageRef = ref(storage, `logos/${filename}`);

      console.log('Uploading to Firebase Storage:', `logos/${filename}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
      });

      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Upload successful, URL:', downloadURL);

      return downloadURL;
    } catch (error: any) {
      console.error('Upload error:', error);
      throw new Error(`Failed to upload image: ${error.message}`);
    }
  }

  /**
   * Upload a favicon to Firebase Storage and return the download URL
   */
  static async uploadFavicon(file: File): Promise<string> {
    try {
      console.log('Starting favicon upload for:', file.name);

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB');
      }

      // Create a unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const filename = `favicon_${timestamp}.${fileExtension}`;
      const storageRef = ref(storage, `favicons/${filename}`);

      console.log('Uploading favicon to Firebase Storage:', `favicons/${filename}`);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
      });

      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('Favicon upload successful, URL:', downloadURL);

      return downloadURL;
    } catch (error: any) {
      console.error('Favicon upload error:', error);
      throw new Error(`Failed to upload favicon: ${error.message}`);
    }
  }

  /**
   * Delete an image from Firebase Storage given its URL
   */
  static async deleteImage(imageUrl: string): Promise<void> {
    try {
      if (!imageUrl || imageUrl.startsWith('data:')) {
        // Skip base64 images or empty URLs
        return;
      }

      // Extract the storage path from the download URL
      const decodedUrl = decodeURIComponent(imageUrl);
      const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);

      if (!pathMatch) {
        console.warn('Could not extract storage path from URL');
        return;
      }

      const storagePath = pathMatch[1];
      const imageRef = ref(storage, storagePath);

      console.log('Deleting image from Firebase Storage:', storagePath);
      await deleteObject(imageRef);
      console.log('Image deleted successfully');
    } catch (error: any) {
      // Don't throw error if file doesn't exist
      if (error.code === 'storage/object-not-found') {
        console.log('Image already deleted or does not exist');
        return;
      }
      console.error('Error deleting image:', error);
      throw new Error(`Failed to delete image: ${error.message}`);
    }
  }
}
